"use client";
import React from "react";

import { usePathname } from "next/navigation";
import { useIntl } from "react-intl";

import {
  COMPONENT_NAMESPACES,
  FOOTER_NAMESPACES,
  useComponentMessages,
} from "ps-design-system/intl-shared-component/IntlGlobalMessages";

import { Footer } from "@/components/organisms/Footer";
import {
  footerNavLinks,
  footerSocialLinks,
} from "@/components/organisms/Footer/data";
import { LocaleSelectorModal } from "@/components/organisms/LocaleSelectorModal";
import { LocaleType as DesignSystemLocaleType } from "@/components/organisms/LocaleSelectorModal/LocaleSelectorModal.types";
import { useLocaleChange } from "~/i18n/hooks/useLocaleChange";
import {
  ACTIVE_LOCALES,
  DESIGN_SYSTEM_ACTIVE_LOCALES,
} from "~/i18n/i18n.consts";
import { Locale } from "~/i18n/i18n.types";
import { useLocaleData } from "~/i18n/IntlProvider";
import { isFooterlessRoute } from "~/utils/routes.utils";

// Add this type definition after the imports
type FooterNamespaceKeys = keyof typeof FOOTER_NAMESPACES;
type StrictFormatMessage = (messageId: FooterNamespaceKeys | string) => string;

const FooterWrapper = () => {
  const intl = useIntl();
  const localeData = useLocaleData();

  const { formatComponentMessage } = useComponentMessages(
    COMPONENT_NAMESPACES.FOOTER,
    intl,
  ) as { formatComponentMessage: StrictFormatMessage }; // Type assertion here

  const {
    handleLocaleChange,
    profileUpdateLoading,
    localeModalOpen,
    setLocaleModalOpen,
  } = useLocaleChange({
    currentLocaleCode: intl.locale,
    source: "footer",
  });

  const pathname = usePathname();
  // Pages with their own sticky bottom bar hide the footer (see FOOTERLESS_ROUTES).
  const isFooterHidden = isFooterlessRoute(pathname ?? "");

  const privacyData = {
    privacyText: formatComponentMessage(FOOTER_NAMESPACES.YOUR_PRIVACY_CHOICES),
    privacyOnClick: () => {
      // No-ops when OneTrust never loaded (blocked CDN); previously this threw, because
      // `window.consentManager` was read unguarded.
      window.OneTrust?.ToggleInfoDisplay();
    },
  };

  if (isFooterHidden) {
    return null;
  }

  return (
    // Positioned, so the footer paints above the homepage hero's fixed panel
    // instead of disappearing behind it.
    <div className="tw-relative">
      <Footer
        footerLinks={footerNavLinks(
          formatComponentMessage,
          localeData.urlPrefix,
          localeData.zendeskUrlLocale,
          (typeof window !== "undefined"
            ? window.analyticsIntegrations?.itemClicked
            : undefined) ?? (() => {}),
        )}
        privacyData={privacyData}
        currentLocale={{
          // FIXME: This is a temporary workaround to convert the design system locale to the LocaleData format.
          url_prefix: localeData.urlPrefix,
          zendesk_url_locale: localeData.zendeskUrlLocale,
          region: localeData.region,
          language: localeData.language,
          country: localeData.country,
          google_locale: localeData.googleLocale,
        }}
        showLocaleSelector={true}
        footerSocialLinks={footerSocialLinks(
          (typeof window !== "undefined"
            ? window.analyticsIntegrations?.itemClicked
            : undefined) ?? (() => {}),
        )}
        LocaleSelectorComp={
          <LocaleSelectorModal
            onSelectLocale={(locale: DesignSystemLocaleType) => {
              // FIXME: This is a temporary workaround to convert the design system locale to the LocaleData format.
              const newLocale = ACTIVE_LOCALES[locale.locale as Locale];
              handleLocaleChange(newLocale);
            }}
            activeLocales={DESIGN_SYSTEM_ACTIVE_LOCALES}
            currentLocale={intl.locale}
            intl={intl}
            buttonClassNames="tw-text-base-white"
            profileUpdateLoading={profileUpdateLoading}
            profileUpdateHasError={false}
            open={localeModalOpen}
            onOpenChange={setLocaleModalOpen}
          />
        }
      />
    </div>
  );
};

export { FooterWrapper };
